/* =============================================================================
   a11y-carousel.js — 30AGear, 2026-08-02
   Audit findings 11 and 13.

   11: three auto-rotating carousels on the homepage (one slideshow + two
       tickers) with no pause control and no `prefers-reduced-motion` support
       (WCAG 2.2.2 Pause, Stop, Hide).
   13: off-screen carousel slides keep focusable controls, so Tab walks into
       "SHOP NOW" buttons on slides nobody can see.

   Deliberately generic: it enhances any initialised Swiper that has an
   autoplay instance, rather than being wired into slideshow.js / ticker.js
   individually. Those two files build their Swipers on different timers
   (DOMContentLoaded vs a 100ms setTimeout), so this polls briefly for
   instances instead of guessing an order.

   VISIBILITY IS COMPUTED GEOMETRICALLY, not read from Swiper's
   `swiper-slide-visible` class: that class only exists when
   `watchSlidesProgress` is on, and the ticker explicitly sets it false.
   Measuring rects works whatever the options are, including with loop
   duplicates.
   ============================================================================= */
(function () {
  "use strict";

  var REDUCE = window.matchMedia("(prefers-reduced-motion: reduce)");
  var enhanced = [];

  function slidesOf(sw) {
    return sw && sw.slides ? Array.prototype.slice.call(sw.slides) : [];
  }

  /* inert on every slide not currently within the viewport of its own track */
  function applyInert(sw) {
    var host = sw.el;
    if (!host) return;
    var hr = host.getBoundingClientRect();
    if (!hr.width) return;
    slidesOf(sw).forEach(function (slide) {
      var r = slide.getBoundingClientRect();
      var overlap = Math.min(r.right, hr.right) - Math.max(r.left, hr.left);
      var visible = overlap > Math.min(8, r.width * 0.5);
      if (visible) {
        slide.removeAttribute("inert");
      } else {
        slide.setAttribute("inert", "");
      }
    });
  }

  var ICON_PAUSE =
    '<svg aria-hidden="true" focusable="false" viewBox="0 0 12 14" width="12" height="14">' +
    '<rect x="1" y="1" width="3.2" height="12" fill="currentColor"/>' +
    '<rect x="7.8" y="1" width="3.2" height="12" fill="currentColor"/></svg>';
  var ICON_PLAY =
    '<svg aria-hidden="true" focusable="false" viewBox="0 0 12 14" width="12" height="14">' +
    '<path d="M1 1l10 6-10 6z" fill="currentColor"/></svg>';

  function addToggle(sw, host, noun) {
    if (host.querySelector(":scope > .a11y-carousel-toggle")) return null;

    var btn = document.createElement("button");
    btn.type = "button";
    btn.className = "a11y-carousel-toggle";

    var paused = false;

    function render() {
      /* Icon only. The visible "Pause" word was covering the ticker text at every
         width, and WCAG 2.2.2 asks for a mechanism that exists, is reachable and
         is perceivable — not for a labelled pill sitting on the content.
         With NO visible text there is nothing for an aria-label to contradict,
         so this is the case where aria-label is the correct way to name a
         control (contrast with the mega-menu triggers and the product-card
         title link, where visible text exists and a label would override it). */
      btn.innerHTML = paused ? ICON_PLAY : ICON_PAUSE;
      btn.setAttribute("aria-label", (paused ? "Play " : "Pause ") + noun);
      btn.setAttribute("title", (paused ? "Play " : "Pause ") + noun);
    }

    /* 30AGear 2026-10-23: a ticker moves as one long linear transition per
       slide (ticker_speed x 1000 ms, 10s live), and autoplay.stop() only cancels
       the NEXT slide, so the text kept moving after Pause. freeze() pins the
       track where it is on screen (the computed mid-transition transform) with
       no transition. thaw() finishes that slide from there at the same pixel
       speed, then hands back to autoplay, so Play never jumps. The hero
       slideshow (discrete slides with a delay) keeps plain stop/start. */
    var isTicker = !!(sw.el && sw.el.closest(".ticker"));
    var thawTimer = null;

    function trackX() {
      var m = getComputedStyle(sw.wrapperEl).transform;
      var v = m && m !== "none" ? m.match(/matrix(3d)?\(([^)]+)\)/) : null;
      if (!v) return sw.getTranslate();
      var p = v[2].split(",").map(parseFloat);
      return v[1] ? p[12] : p[4];
    }

    function freeze() {
      var target = sw.translate;
      var x = trackX();
      sw.autoplay.stop();
      /* Swiper's visibilitychange handler restarts autoplay whenever this is
         set, so a frozen ticker would start again after a tab switch. */
      sw.autoplay.paused = false;
      sw.setTransition(0);
      sw.setTranslate(x);
      sw.__a11yTarget = Math.abs(target - x) > 0.5 ? target : null;
    }

    function thaw() {
      var target = sw.__a11yTarget;
      sw.__a11yTarget = null;
      if (target === null || target === undefined) {
        sw.autoplay.start();
        return;
      }
      var step =
        Math.abs(sw.slidesGrid[1] - sw.slidesGrid[0]) ||
        Math.abs(target - sw.translate);
      var ms = Math.max(
        50,
        Math.round(
          sw.params.speed * Math.min(1, Math.abs(target - sw.translate) / step)
        )
      );
      var done = false;
      function finish(e) {
        if (done || (e && e.target !== sw.wrapperEl)) return;
        done = true;
        sw.wrapperEl.removeEventListener("transitionend", finish);
        clearTimeout(thawTimer);
        if (paused) return;
        /* the slide frozen mid-way never fired transitionend, so make sure
           Swiper does not still think it is animating (loop mode refuses to
           slide while it is) */
        sw.animating = false;
        sw.autoplay.start();
      }
      sw.wrapperEl.addEventListener("transitionend", finish);
      thawTimer = setTimeout(finish, ms + 150);
      sw.setTransition(ms);
      sw.setTranslate(target);
    }

    function setPaused(next) {
      paused = next;
      if (!sw.autoplay) return;
      if (isTicker) {
        if (paused) freeze();
        else thaw();
      } else if (paused) sw.autoplay.stop();
      else sw.autoplay.start();
      render();
    }

    /* The slideshow is built with `pauseOnMouseEnter`, so Swiper RESUMES autoplay
       on mouseleave — which silently undid an explicit pause the moment the
       pointer moved off the control. Measured: after clicking Pause and moving
       away, autoplay.running was true again and the slide index kept advancing,
       while the button still read "Pause".
       An explicit pause has to outrank Swiper's own resume, so re-stop whenever
       it tries to start while we are in the paused state. */
    sw.on("autoplayResume", function () {
      if (paused && sw.autoplay) sw.autoplay.stop();
    });
    sw.on("autoplayStart", function () {
      if (paused && sw.autoplay) sw.autoplay.stop();
    });

    btn.addEventListener("click", function () {
      setPaused(!paused);
    });

    render();
    host.appendChild(btn);

    return {
      setPaused: setPaused,
      isPaused: function () {
        return paused;
      },
    };
  }

  function enhance(sw, noun) {
    if (!sw || sw.__a11yCarousel || !sw.autoplay) return;
    sw.__a11yCarousel = true;

    /* the section wrapper is a better button host than the overflow-hidden
       .swiper track, which would clip an absolutely positioned control */
    var host =
      sw.el.closest(".slideshow, .ticker, .shopify-section") || sw.el;
    if (getComputedStyle(host).position === "static") {
      host.style.position = "relative";
    }

    var ctl = addToggle(sw, host, noun);

    /* prefers-reduced-motion: reduce -> autoplay off, control shows "Play" so
       the user can still opt in. Honoured live, not just at first paint. */
    function applyMotionPreference() {
      if (REDUCE.matches) {
        if (ctl) ctl.setPaused(true);
        else if (sw.autoplay) sw.autoplay.stop();
      }
    }
    applyMotionPreference();
    if (REDUCE.addEventListener) {
      REDUCE.addEventListener("change", applyMotionPreference);
    } else if (REDUCE.addListener) {
      REDUCE.addListener(applyMotionPreference);
    }

    applyInert(sw);
    ["slideChange", "transitionEnd", "resize", "setTranslate"].forEach(function (
      ev
    ) {
      sw.on(ev, function () {
        applyInert(sw);
      });
    });

    enhanced.push(sw);
  }

  function scan() {
    document.querySelectorAll(".swiper").forEach(function (el) {
      var sw = el.swiper;
      if (!sw || !sw.autoplay || sw.__a11yCarousel) return;
      var noun = el.closest(".ticker")
        ? "the scrolling announcement banner"
        : "the rotating banner";
      enhance(sw, noun);
    });
  }

  /* slideshow.js runs on DOMContentLoaded, ticker.js on a 100ms timer, and
     section re-renders in the theme editor rebuild both. Poll briefly rather
     than depend on an ordering none of them guarantees. */
  var tries = 0;
  function poll() {
    scan();
    if (++tries < 25) setTimeout(poll, 200);
  }
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", poll);
  } else {
    poll();
  }
  document.addEventListener("shopify:section:load", function () {
    tries = 0;
    setTimeout(poll, 150);
  });
})();
